<template lang="pug">
.overall-table
  table.competitor-row(v-if="results.length > 0 && showResults")
    tbody
      tr
        th
          div.toggle-sort(@click="updateSort('rank', undefined, sortOrder === 'asc' ? 'desc' : 'asc')" :class="{active: sortMeasurementKey === 'rank'}") Rank
            Icon(:name="sortOrder === 'asc' ? 'arrow-down' : 'arrow-up'" fill="#000000" v-if="sortMeasurementKey === 'rank'")
        th
          .left-align Competitor
        th
        th
          div Gym
        th
          div.toggle-sort(@click="updateSort('points', undefined, sortOrder === 'asc' ? 'desc' : 'asc')" :class="{active: sortMeasurementKey === 'points'}") Total Points
            Icon(:name="sortOrder === 'asc' ? 'arrow-down' : 'arrow-up'" fill="#000000" v-if="sortMeasurementKey === 'points'")
        th(v-for="(name, index) in eventNames")
          div.toggle-sort(@click="updateSort(name.eventName, (index + 1), sortOrder === 'asc' ? 'desc' : 'asc')" :class="{active: sortMeasurementKey === name.eventName}") {{name.headerName}}
            Icon(:name="sortOrder === 'asc' ? 'arrow-down' : 'arrow-up'" fill="#000000" v-if="sortMeasurementKey === name.eventName")
      tr.competitor(v-for="competitor in results")
        td.rank {{competitor.rank}}
        td.name
          span.name(@click="handleAthleteModal(competitor)") {{competitor.name}}
        td.flag
          StaticImage.flag-thumb(
            v-if="competitor.flag"
            :src="`cms/rogue%20challenge/flags/${competitor.flag}`"
            :alt="flagAlt(competitor.flag, $rogue)"
            :autoHeight="true"
            :width="32"
            :height="20"
          )
        td.gym {{competitor.gym}}
        td.points {{competitor.points}}
        td.results(v-for="eventResult in competitor.results")
          span.results-container
            span {{eventResult.points}}
  .no-results(v-else) {{ __('No Results') }}
  Modal(v-if="showModal" @close="closeModal")
    .athlete-card
      .top
        .left
          .avatar
            StaticImage.avatar-thumb(
              v-if="competitorInfo.avatar"
              :src="competitorInfo.avatar"
              alt=""
              :autoHeight="true"
            )
          .info
            .name {{competitorInfo.name}}
            .gym {{competitorInfo.gym}}
            .score
              .rank {{handleOrdinal(competitorInfo.rank)}}
                span &nbsp;Overall
              .points {{competitorInfo.points}}
                span &nbsp;Points
        .right
          .flag
            StaticImage.flag-thumb(
              v-if="competitorInfo.flag"
              :src="`cms/rogue%20challenge/flags/${competitorInfo.flag}`"
              :alt="flagAlt(competitorInfo.flag, $rogue)"
              :autoHeight="true"
              :width="32"
              :height="20"
            )
      .results
        table.results
          thead
            tr
              th(v-for="heading in ['Event', 'Score', 'Points', 'Place']") {{ __(heading) }}
          tbody
            tr(v-for="(event, key) in competitorInfo.results")
              td.event {{ key }}
              td.score {{ formatResult({value: event.value, unit: event.unit}) }}
              td.points {{ event.points }}
              td.place {{ event.place }}
</template>
<script setup lang="ts">
import { flagAlt } from 'sisu/js/helpers'
import { ref, computed, type PropType } from 'vue'
import type { IEvent, IDivision, CompetitorResultInfo, EventResult } from './leaderboard-types'
import { buildTime  } from './utils'

const props = defineProps({
  selectedEvent: {
    type     : Object as PropType<IEvent>,
    required : true,
  },
  selectedDivision: {
    type     : Object as PropType<IDivision>,
    required : true,
  },
  results: {
    type    : Array,
    default : () => ([]),
  },
  events: {
    type    : Array as PropType<IEvent[]>,
    default : () => ([]),
  },
  name: {
    type    : String,
    default : '',
  },
  sortOrder: {
    type    : String,
    default : 'asc',
  },
  sortMeasurementKey: {
    type    : String,
    default : '',
  },
  sortChallengeNumber: {
    type    : Number,
    default : undefined,
  },
  showResults: {
    type    : Boolean,
    default : false,
  },
})

const emit = defineEmits(['updateSort'])
const showModal = ref(false)
const competitorInfo = ref<CompetitorResultInfo | null>(null)

const eventNames = computed(() => props.selectedEvent.overallResultColumns?.map(key => ({ headerName: key.challengeName, eventName: key.challengeName.replace(/ /g, '_') })))

function handleAthleteModal(competitor: CompetitorResultInfo) {
  competitorInfo.value = competitor
  showModal.value = true
}

function updateSort(sortMeasurementKey: string, sortChallengeNumber: number, sortOrder: string) {
  emit('updateSort', { sortMeasurementKey, sortChallengeNumber, sortOrder })
}

function closeModal() {
  showModal.value = false
}

function handleOrdinal(i: number) {
  const j = i % 10
  const k = i % 100
  if (j === 1 && k !== 11) {
    return `${i}st`
  }
  if (j === 2 && k !== 12) {
    return `${i}nd`
  }
  if (j === 3 && k !== 13) {
    return `${i}rd`
  }

  return `${i}th`
}

function formatResult(eventResult: EventResult) {
  const unit = eventResult?.unit
  const value = eventResult?.value
  let formattedResult = `${value} ${unit || ''}`.trim()

  if (value === 0 || value === undefined) {
    return '-'
  }

  if (unit === 'seconds' || unit === 's' || unit === 'time') {
    formattedResult = buildTime(value)
  } else if (unit === 'msec' || unit === 'msecs') {
    formattedResult = buildTime(value / 1000)
  }

  return formattedResult
}

</script>

<style lang="scss" scoped>
.overall-table {
  overflow-x: scroll;
}

table.competitor-row {
  border: none;
  max-width: 100%;
}

table.competitor-row tr th {
  border-bottom: 1px solid $color-black;
  text-transform: uppercase;
  font-size: $size-s;
  padding: 0;
}

table.competitor-row tr th div {
  padding: $size-xxs;
}

table.competitor-row tr th .left-align {
  text-align: left;
}

table.competitor-row tr td {
  border-bottom: 1px solid $color-gray-light;
}

table.competitor-row .toggle-sort {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  white-space: nowrap;
}

table.competitor-row th .toggle-sort.active {
  background: $color-gray-light;
}

table.competitor-row th .toggle-sort.active .icon {
  height: $size-xxs;
  width: $size-xxs;
  margin-left: 6px;
  margin-top: 3px;
}

table.competitor-row td:first-child {
  z-index: 1;
}

.competitor .name {
  position: relative;
  text-align: left;
  min-width: 150px;
  cursor: pointer;
}

.competitor td.name {
  padding: 0px 12px;
}

.competitor td.name span.name {
  margin-right: $size-l;
  padding: $space-xxs 0;
}

.competitor .results-container {
  position: relative;
}

.flag-thumb {
  width: $space-l;
  max-height: $space-s;
}

.no-results {
  margin: 40px;
  text-align: center;
}

.modal-body .athlete-card .top {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.modal-body .athlete-card .top .left {
  display: flex;
  flex-direction: row;
}

.modal-body .athlete-card .top .left .avatar-thumb {
  width: 75px;
  max-height: 75px;
  margin-right: 20px;
}

.modal-body .athlete-card .top .left .info {
  display: flex;
  flex-direction: column;
}

.modal-body .athlete-card .top .left .info .name {
  text-transform: uppercase;
  font-weight: bold;
  font-size: 20px;
  font-family: $font-header;
}

.modal-body .athlete-card .top .left .info .score {
  display: flex;
  flex-direction: row;
  margin-top: 12px;
}

.modal-body .athlete-card .top .left .info .rank,
.modal-body .athlete-card .top .left .info .points {
  font-size: 18px;
  font-weight: bold;
  margin-right: 20px;
}

.modal-body .athlete-card .top .left .info .rank span,
.modal-body .athlete-card .top .left .info .points span {
  font-size: 16px;
  font-weight: normal;
}
</style>
